/**
 * What each Data Ingestion connector is known to store, and what it never
 * carries. Kept in step with `.cursor/skills/add-brand-scraper/source-fields.md`.
 * A field listed under `unavailable` is filled by a price sheet or another
 * feed, or left empty. It is not a scraper bug.
 *
 * MAP, dealer cost and trade credit are never taken from a storefront.
 */

export interface IngestorCoverage {
  /** Where the product fields come from. */
  source: string;
  /** Stored when the source has them. */
  publishes: string[];
  /** This source does not carry these. */
  unavailable: string[];
  /** No row has been written for this connector. */
  unrecorded?: boolean;
}

const PRICE_SHEET = 'MAP, dealer cost and trade credit';
const PRICE_REFRESH_CONTENT = 'Description, specs, images and weight on a price refresh';

const SHOPIFY_CATALOG = [
  'Name, description and images',
  'SKU, price, compare-at price and availability',
  'Boxed shipping weight, tags and product type',
];

const SHOPIFY_DESCRIPTION_CONTENT =
  'Specs, features, in the box, videos, documents and net weight, when the product description includes them';

const SHOPIFY_PAGE_CONTENT =
  'Specs, features, in the box, videos, documents and net weight from the product page on a full scrape';

const SHOPIFY_BARCODE =
  'Barcode, when the product file has a 12- or 13-digit code for that SKU';

const SHOPIFY_GAPS = [
  PRICE_SHEET,
  'A barcode the store does not publish for that SKU',
  PRICE_REFRESH_CONTENT,
];

function shopify(options?: {
  /** The subclass also reads the product page on a full scrape. */
  productPage?: boolean;
  publishes?: string[];
  unavailable?: string[];
  source?: string;
}): IngestorCoverage {
  return {
    source: options?.source ?? 'Shopify store catalog',
    publishes:
      options?.publishes ??
      (options?.productPage
        ? [...SHOPIFY_CATALOG, SHOPIFY_BARCODE, SHOPIFY_PAGE_CONTENT]
        : [...SHOPIFY_CATALOG, SHOPIFY_BARCODE, SHOPIFY_DESCRIPTION_CONTENT]),
    unavailable: options?.unavailable ?? SHOPIFY_GAPS,
  };
}

const OFFICIAL_PAGE: IngestorCoverage = {
  source: 'The brand’s official product page',
  publishes: [
    'Name, price, description, specs and images',
    'Barcode, only when that page prints one for the SKU',
  ],
  unavailable: [PRICE_SHEET, 'A barcode the page does not print'],
};

/**
 * One entry per brand on the ingestion console (`SUPPORTED_BRANDS`).
 * `ingestorCoverage.test.ts` fails when that list gains a brand with no row.
 */
export const INGESTOR_COVERAGE: Readonly<Record<string, IngestorCoverage>> = {
  sony: OFFICIAL_PAGE,
  nikon: OFFICIAL_PAGE,
  canon: {
    source: 'Canon shop pages',
    publishes: [
      'Name, price, description, images and specs',
      'Barcode, when the product page embeds a GTIN for that SKU',
    ],
    unavailable: [PRICE_SHEET, 'A barcode on a product page that does not embed a GTIN'],
  },
  fujifilm: {
    source: 'Fujifilm US shop, with specs from the specifications page',
    publishes: [
      'Price, SKU, availability, images and barcode from the US shop',
      'Specs from the specifications page',
      'Description from the shop, with the marketing page only as a fallback for wording',
    ],
    unavailable: [
      PRICE_SHEET,
      'Price or a barcode from a marketing page',
      'A Canadian shop. There isn’t one',
    ],
  },
  benro: {
    source: 'Benro product API',
    publishes: ['Name, price, description, images, features and specs'],
    unavailable: [PRICE_SHEET, 'A barcode. The API payload has no barcode field'],
  },
  panasonic: {
    source: 'Panasonic Shopify product file',
    publishes: [
      'Name, price, images and SKU',
      'Barcode on a full scrape, or when a product is first discovered',
      'Specs from Panasonic’s help pages when the store page does not include them',
    ],
    unavailable: [
      PRICE_SHEET,
      'Description, specs and images on a price refresh. That run writes price and stock only',
      'A barcode added to the page later, until the next full scrape',
    ],
  },
  nisi: {
    source: 'NiSi WooCommerce store. One North American catalog',
    publishes: [
      'Name, price, description and images',
      'Barcode and the USA product code (NIM- or NIP-) from the product page on a full scrape',
    ],
    unavailable: [
      PRICE_SHEET,
      'A barcode on a price refresh. The product page is not re-read',
      'A barcode when the product page has no barcode line',
      'A separate Canadian price. The US dollar catalog is written for both regions',
    ],
  },
  rockynook: {
    source: 'Rocky Nook WooCommerce store (the publisher’s own site)',
    publishes: [
      'Title, print-edition list price, description, cover image and the ISBN-13, stored as the barcode',
      'Author, subtitle, page count, trim size, cover and publication date from the product page on a full scrape',
    ],
    unavailable: [
      PRICE_SHEET,
      'A weight. The site publishes none for any title',
      'The ebook and print-and-ebook editions. Only the print book is staged',
      'A forthcoming title until the store lets it be bought',
      'Author and subtitle on a price refresh. The product page is not re-read',
    ],
  },
  smallrig: {
    source: 'SmallRig catalog API',
    publishes: ['SKU, price, description and barcode', 'An EAN-13, stored as the UPC'],
    unavailable: [
      PRICE_SHEET,
      'A barcode copied onto a bundle SKU from another product',
    ],
  },
  sigma: {
    source: 'Sigma US product pages, and the Canadian distributor’s Shopify store',
    publishes: [
      'Name, description and product type',
      'Variant barcode: 12 digits stored as a UPC, 13 as an EAN',
    ],
    unavailable: [PRICE_SHEET, 'A barcode on a variant whose barcode field is blank'],
  },
  omsystem: {
    source: 'OM System product page data',
    publishes: ['SKU, manufacturer code, barcode (GTIN-12), list price, sale price and availability'],
    unavailable: [PRICE_SHEET, 'A barcode when the page data has no GTIN-12'],
  },
  'ricoh-imaging': {
    source: 'Ricoh Imaging US store. Canada is a retailer catalog',
    publishes: [
      'Name, price, description, images and specs from the US store',
      'Barcode, only when the US page prints one',
    ],
    unavailable: [
      PRICE_SHEET,
      'An official Canadian catalog. Canada comes from Don’s Photo, a retailer',
      'A barcode the US page does not print',
    ],
  },
  tamron: {
    source: 'Tamron Americas store, including Canadian prices on the same site',
    publishes: [
      'Name, price, description, specs and images',
      'One product per lens mount, with that mount’s item code',
      'Barcode, only when the page prints one',
    ],
    unavailable: [
      PRICE_SHEET,
      'A Canadian price for a mount the site does not sell in Canada',
      'A barcode the page does not print',
    ],
  },
  manfrotto: {
    source: 'Manfrotto product pages. The site does not sell direct',
    publishes: [
      'Title, product code, features, overview, specs (including weight), gallery and PDFs',
      'US barcode from the retailer widget, when that widget can be read',
    ],
    unavailable: [
      'A price. The page shows 0 and points to a retailer, and the widget’s street prices are not used',
      'MAP, dealer cost and trade credit',
      'Anything on a price refresh. There is no price to refresh',
      'A Canadian catalog. The barcode widget is US only',
    ],
  },
  godox: {
    source: 'Godox US product pages',
    publishes: ['SKU, barcode, price, description and the spec table'],
    unavailable: [
      PRICE_SHEET,
      'A barcode when the page data has none',
      'A Canadian store. There isn’t one',
      'The corporate site. It publishes no prices and is not a catalog',
    ],
  },
  promaster: {
    source: 'Not this storefront. The dealer feed is the catalog',
    publishes: ['Nothing on a scheduled run. This storefront scraper is turned off'],
    unavailable: [
      'The catalog, MAP and warehouse UPC. Those come from the dealer feed',
      'A UPC or MPN filled in on a price refresh. The warehouse fill runs on a content run only',
    ],
  },

  rokinon: shopify({ productPage: true }),
  viltrox: shopify({
    productPage: true,
    publishes: [
      ...SHOPIFY_CATALOG,
      'Barcode from the product file, matched to that variant. An EAN-13 is stored as the UPC',
      'Specs from the product page, including mount comparison tables, on a full scrape',
    ],
    unavailable: [
      PRICE_SHEET,
      'A barcode when the variant has none',
      'A handle used in place of a manufacturer SKU. It is not treated as one',
      PRICE_REFRESH_CONTENT,
    ],
  }),
  hasselblad: shopify(),
  westcott: shopify({ productPage: true }),
  aputure: shopify(),
  peak_design: shopify({
    productPage: true,
    publishes: [
      ...SHOPIFY_CATALOG,
      'Barcode from the product page first, then the product file',
      SHOPIFY_PAGE_CONTENT,
    ],
    unavailable: [
      PRICE_SHEET,
      'A barcode on a bundle, or on a listing whose page has none',
      PRICE_REFRESH_CONTENT,
    ],
  }),
  nomatic: shopify({ productPage: true }),
  pgytech: shopify(),
  wandrd: shopify({
    productPage: true,
    publishes: [
      ...SHOPIFY_CATALOG,
      'Barcode, after the catalog is scanned, and only when that code belongs to one SKU',
      SHOPIFY_PAGE_CONTENT,
    ],
    unavailable: [
      PRICE_SHEET,
      'A barcode the site gives to more than one SKU',
      'A barcode on most variants. They publish none',
      PRICE_REFRESH_CONTENT,
    ],
  }),
  polaroid: shopify(),
  think_tank: shopify(),
  tiffen: shopify({ productPage: true }),
  smallhd: shopify(),
  pelican: shopify({
    unavailable: [...SHOPIFY_GAPS, 'An 11-digit code. Pelican often prints one, and it is not stored'],
  }),
  nanuk: shopify({ productPage: true }),
  tokina: shopify(),
  meike: shopify({
    productPage: true,
    unavailable: [
      PRICE_SHEET,
      'A barcode. This store publishes none',
      PRICE_REFRESH_CONTENT,
    ],
  }),
  cinestill: shopify({
    productPage: true,
    publishes: [
      ...SHOPIFY_CATALOG,
      'Barcode from the product file. An EAN-13 is stored as the UPC',
      SHOPIFY_PAGE_CONTENT,
    ],
  }),
  vivitar: shopify(),
  phottix: shopify({ productPage: true }),
  'superior-seamless': {
    source: 'Superior Seamless WooCommerce store (the official background-paper site)',
    publishes: [
      'Colour name, description and images from the store API',
      'Roll sizes and weights from the product page; one colour and roll size is one product',
    ],
    unavailable: [
      PRICE_SHEET,
      'A price. The site is a dealer-sold catalog and publishes none, so a price refresh only re-checks that the colour still exists',
      'A barcode. The site publishes none',
      'Anything while the site’s bot protection challenges the request; a blocked full run stages nothing',
    ],
  },
};

const UNRECORDED: IngestorCoverage = {
  source: 'Not recorded',
  publishes: [],
  unavailable: [],
  unrecorded: true,
};

export function ingestorCoverage(brandId: string): IngestorCoverage {
  return INGESTOR_COVERAGE[brandId] ?? UNRECORDED;
}
